import { CircleAlert, CircleCheck, FolderOpen, History, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTimecode } from '@/core/time/format';
import { MEDIA_ACCEPT } from '@/state/assets';
import { useEditorStore } from '@/state/editor-store';
import { age, type ProjectSummary } from '@/state/projects/project';
import { missingOf, useProjectsStore, type Continuing } from '@/state/projects/projects-store';
import { ConfirmDialog } from '../Dialog';
import { LAYER_ICONS } from '../editor/media/layer-icons';
import { useFilePicker } from '../editor/media/use-file-picker';
import { Button, IconButton, Spinner } from '../primitives';

/**
 * Edits kept on this device, offered back on the home page. Opening one asks
 * only for the files the browser no longer holds, and matches them up itself.
 */
export function ContinueProjects() {
  const { t } = useTranslation();
  const projects = useProjectsStore((state) => state.projects);
  const continuing = useProjectsStore((state) => state.continuing);
  const reaching = useProjectsStore((state) => state.reaching);
  const opening = useEditorStore((state) => state.load.status === 'loading');
  const { refresh, remove, continue: pickUp } = useProjectsStore.getState();
  const [deleting, setDeleting] = useState<ProjectSummary | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    void refresh();
    setNow(Date.now());
  }, [refresh]);

  if (!projects || projects.length === 0) return null;
  return (
    <section aria-labelledby="projects-title" className="rounded-2xl bg-panel px-4 py-3.5 ring-1 ring-line ring-inset">
      <h2 id="projects-title" className="flex items-center gap-2 text-[15px] font-semibold text-fg">
        <History size={16} className="shrink-0 text-accent-text" />
        {t('projects.title')}
      </h2>
      <ul className="mt-2 divide-y divide-line">
        {projects.map((project) => {
          const open = continuing?.project.id === project.id;
          const busy = (open && opening) || reaching === project.id;
          return (
            <li key={project.id} className="py-2.5">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <Poster poster={project.poster} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-fg">{project.name}</p>
                  <p className="text-[13px] text-fg-muted tabular">
                    <ProjectMeta project={project} now={now} />
                  </p>
                </div>
                <span className="flex items-center gap-1">
                  <Button size="sm" variant="secondary" disabled={opening || open || reaching !== null} aria-busy={busy} onClick={() => void pickUp(project.id)}>
                    {busy ? <Spinner /> : null}
                    {t('projects.continue')}
                  </Button>
                  <IconButton label={t('projects.delete')} disabled={opening || reaching !== null} onClick={() => setDeleting(project)} className="hover:text-danger">
                    <Trash2 size={16} />
                  </IconButton>
                </span>
              </div>
              {open && continuing && <Relink continuing={continuing} />}
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        open={deleting !== null}
        title={t('projects.deleteTitle')}
        body={deleting ? t(deleting.recordingId ? 'projects.deleteBodyRecording' : 'projects.deleteBody', { name: deleting.name }) : ''}
        confirmLabel={t('projects.delete')}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) void remove(deleting.id);
          setDeleting(null);
        }}
      />
    </section>
  );
}

function ProjectMeta({ project, now }: { readonly project: ProjectSummary; readonly now: number }) {
  const { t, i18n } = useTranslation();
  const { value, unit } = age(project.updatedAt, now);
  const when = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' }).format(-value, unit);
  return (
    <>
      {t('projects.meta', { clips: t('projects.clips', { count: project.clipCount }), duration: formatTimecode(project.durationSec), when })}
    </>
  );
}

/** The opened video's picture, from the frames the editor read. */
function Poster({ poster }: { readonly poster: Blob | null }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!poster) return setUrl(null);
    const next = URL.createObjectURL(poster);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [poster]);
  return (
    <span className="aspect-video w-20 shrink-0 overflow-hidden rounded-md bg-elevated ring-1 ring-line">
      {url && <img src={url} alt="" className="size-full object-cover" />}
    </span>
  );
}

/** What the project still needs, and a way to hand it over. */
function Relink({ continuing }: { readonly continuing: Continuing }) {
  const { t } = useTranslation();
  const { provide, open, cancel } = useProjectsStore.getState();
  const picker = useFilePicker(MEDIA_ACCEPT, (files) => void provide(files));
  const missing = missingOf(continuing);
  const recordingGone = continuing.project.recordingId !== null && continuing.recording === null;
  const ready = missing.length === 0;
  return (
    <div role="group" aria-labelledby="relink-title" className="mt-3 rounded-xl bg-elevated px-3.5 py-3 sm:ml-24">
      <p id="relink-title" className="text-sm font-medium text-fg">
        {t('projects.relink.title')}
      </p>
      <p className="mt-0.5 text-[13px] leading-relaxed text-fg-muted">{recordingGone ? t('projects.relink.recordingGone') : t('projects.relink.body')}</p>
      <ul className="mt-2.5 space-y-1.5" aria-live="polite">
        {continuing.needed.map((asset) => {
          const found = continuing.files.has(asset.id);
          return (
            <li key={asset.id} className="flex items-center gap-2 text-[13px]">
              <span className="shrink-0 text-fg-subtle">{LAYER_ICONS[asset.kind](14)}</span>
              <span className={`min-w-0 flex-1 truncate ${found ? 'text-fg-muted' : 'text-fg'}`}>{asset.name}</span>
              {found ? (
                <span className="flex shrink-0 items-center gap-1 text-accent-text">
                  <CircleCheck size={14} />
                  {t('projects.relink.found')}
                </span>
              ) : (
                <span className="shrink-0 text-fg-subtle">{t('projects.relink.missing')}</span>
              )}
            </li>
          );
        })}
      </ul>
      {continuing.strangers.length > 0 && (
        <p className="mt-2.5 flex items-start gap-1.5 text-[13px] text-fg-muted">
          <CircleAlert size={14} className="mt-0.5 shrink-0" />
          {t('projects.relink.strangers', { count: continuing.strangers.length, names: continuing.strangers.join(', ') })}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {ready ? (
          <Button size="sm" variant="primary" onClick={() => void open()}>
            {t('projects.relink.open')}
          </Button>
        ) : (
          <Button size="sm" variant="primary" icon={continuing.matching ? <Spinner /> : <FolderOpen size={15} />} disabled={continuing.matching} onClick={picker.pick}>
            {continuing.matching ? t('projects.relink.matching') : t('projects.relink.pick', { count: missing.length })}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={cancel}>
          {t('common.cancel')}
        </Button>
      </div>
      {picker.input}
    </div>
  );
}
